import { FC, useEffect, useState } from 'react';

import { Box, FormLabel, Grid, Typography } from '@mui/material';
import { FormikProps, useField } from 'formik';

import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { CustomCounter, FieldInput } from 'src/components/FormikComponents';
import { InputWithSearch } from 'src/components/InputWithSearch';
import { AddressListModal } from 'src/components/Modals';
import { usePrincipalAddressContext } from 'src/contexts/PrincipalAddressProvider';
import { useAddressLookUp } from 'src/hooks/useAddressLookup';
import { Principal } from 'src/types/AddMerchant/merchantInfo';
import { calculateTimeAtAddressInYears } from 'src/utils/utils';
import { Address } from 'types/graphql';

interface AddressSection {
    formik: FormikProps<any>;
    heading: string;
    postalLabel: string;
    accessor: string;
    useCase?: 'just-data-capturing' | 'auto-populate-previous';
    addressIndex?: number;
    principals?: Principal[];
    principal?: Principal;
    principalIndex?: number;
    disabled?: boolean;
}

const AddressSection: FC<AddressSection> = ({
    formik,
    heading,
    postalLabel,
    addressIndex,
    principals,
    principal,
    principalIndex,
    accessor,
    disabled,
}) => {
    const [{ value }] = useField(`principals[${principalIndex}].${accessor}`);
    const [openAddressModal, setOpenAddressModal] = useState(false);
    const { findAddress, addresses } = useAddressLookUp();
    const { onAddressIsValid } = usePrincipalAddressContext();
    const [formattedAddress, setFormattedAddress] = useState<string | null>(null);

    const onFindAddress = async (value) => {
        findAddress(value, () => {
            return setOpenAddressModal(true);
        });
    };

    useEffect(() => {
        const timeAtAddress = value?.timeAtAddress ?? { years: 0, months: 0 };

        const timeInMonths =
            calculateTimeAtAddressInYears(timeAtAddress.years || 0, timeAtAddress.months || 0) * 12;

        if (timeInMonths > 0) {
            onAddressIsValid &&
                onAddressIsValid(addressIndex, principals, principal, principalIndex);
        }
    }, [value?.timeAtAddress?.years, value?.timeAtAddress?.months]);

    const onRetrievalOfAddress = (address: Address, formattedAddress: string) => {
        formik.setFieldValue(
            `principals[${principalIndex}].${accessor}.addressLine1`,
            address.line1
        );
        formik.setFieldValue(
            `principals[${principalIndex}].${accessor}.eircode`,
            address.postalCode
        );

        formik.setFieldValue(
            `principals[${principalIndex}].${accessor}.addressLine2`,
            address.line2
        );
        formik.setFieldValue(`principals[${principalIndex}].${accessor}.city`, address.city);
        formik.setFieldValue(`principals[${principalIndex}].${accessor}.county`, address.province);
        formik.setFieldValue(
            `principals[${principalIndex}].${accessor}.country`,
            address.countryName
        );

        setFormattedAddress(formattedAddress);
    };

    return (
        <>
            <Typography color="#F18F34" fontSize="20px" lineHeight="30px" fontWeight="500">
                {heading}
            </Typography>
            <Grid item xs={12} md={12}>
                <InputWithSearch
                    searchText="Search"
                    onSearch={onFindAddress}
                    placeholder="Search"
                    label="Search"
                    fieldName="registeredAddress"
                    maskedValue={formattedAddress}
                    clearMaskedValue={() => {
                        return setFormattedAddress('');
                    }}
                />
            </Grid>
            <Box display="flex" gap="24px">
                <Box flex={1}>
                    <FieldInput
                        name={`principals[${principalIndex}].${accessor}.addressLine1`}
                        label="Address Line 1"
                        disabled={disabled}
                    />
                </Box>
                <Box flex={1}>
                    <FieldInput
                        name={`principals[${principalIndex}].${accessor}.addressLine2`}
                        label="Address Line 2"
                        disabled={disabled}
                    />
                </Box>
            </Box>
            <Box display="flex" gap="24px">
                <Box flex={1}>
                    <FieldInput
                        name={`principals[${principalIndex}].${accessor}.city`}
                        label="Town/City"
                        disabled={disabled}
                    />
                </Box>
                <Box flex={1}>
                    <FieldInput
                        name={`principals[${principalIndex}].${accessor}.county`}
                        label="County"
                        disabled={disabled}
                    />
                </Box>
            </Box>
            <Box display="flex" gap="24px">
                <Box flex={1}>
                    <CountrySelectInput
                        setFieldValue={formik.setFieldValue}
                        fieldName={`principals[${principalIndex}].${accessor}.country`}
                        disabled={disabled}
                    />
                </Box>
                <Box flex={1}>
                    <FieldInput
                        name={`principals[${principalIndex}].${accessor}.eircode`}
                        label={postalLabel}
                        disabled={disabled}
                    />
                </Box>
            </Box>
            <Box>
                <FormLabel
                    sx={{
                        marginBottom: '8px',
                        color: '#000',
                        fontWeight: 600,
                    }}
                >
                    Time at Address
                </FormLabel>
                <Box display="flex" gap="16px">
                    <CustomCounter
                        name={`principals[${principalIndex}].${accessor}.timeAtAddress.years`}
                        placeholder="Years"
                        disabled={disabled}
                    />
                    <CustomCounter
                        name={`principals[${principalIndex}].${accessor}.timeAtAddress.months`}
                        placeholder="Months"
                        disabled={disabled}
                    />
                </Box>
            </Box>

            <AddressListModal
                isOpen={openAddressModal}
                onClose={() => {
                    return setOpenAddressModal(false);
                }}
                addressList={addresses}
                onSelectedAddress={onRetrievalOfAddress}
            />
        </>
    );
};
AddressSection.defaultProps = {
    useCase: 'just-data-capturing',
};

export default AddressSection;
